'use client';

import { useSuspenseQuery } from '@apollo/client/react';
import { FC, PropsWithChildren, useMemo } from 'react';

import { GetPublicationsForMeQuery } from '@/.gql/graphql';
import { GET_PUBLICATIONS_FOR_ME } from '@/.graphql';

import { createCustomContext } from './context';

interface IPublicationsForMeProps {
  publicationsForMe: GetPublicationsForMeQuery['getPublicationsForMe']['data'];
  hasMorePublicationsForMe: boolean;
  isLoadingPublicationsForMe: boolean;
  loadMorePublicationsForMe: () => void;
}

const [PublicationsForMeContext, usePublicationsForMeContext] =
  createCustomContext<IPublicationsForMeProps>({
    name: 'PublicationsForMeContext',
    strict: false,
  });

export { usePublicationsForMeContext };

export const PublicationsForMeProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    data: imageData,
    fetchMore: fetchMorePublicationsForMes,
    networkStatus,
  } = useSuspenseQuery(GET_PUBLICATIONS_FOR_ME, {
    variables: {
      cursor: null,
    },
  });

  const publicationsForMe = useMemo(
    () => imageData?.getPublicationsForMe?.data || [],
    [imageData],
  );

  const hasMorePublicationsForMe = !!imageData?.getPublicationsForMe.nextCursor;

  const isLoadingPublicationsForMe = networkStatus === 3;
  const loadMorePublicationsForMe = useMemo(
    () => () => {
      if (!hasMorePublicationsForMe || isLoadingPublicationsForMe) {
        return;
      }

      return fetchMorePublicationsForMes({
        variables: {
          cursor: imageData?.getPublicationsForMe.nextCursor,
        },
      });
    },
    [
      hasMorePublicationsForMe,
      isLoadingPublicationsForMe,
      imageData,
      fetchMorePublicationsForMes,
    ],
  );

  const contextValue = useMemo(
    () => ({
      publicationsForMe,
      hasMorePublicationsForMe,
      loadMorePublicationsForMe,
      isLoadingPublicationsForMe,
    }),
    [
      publicationsForMe,
      hasMorePublicationsForMe,
      loadMorePublicationsForMe,
      isLoadingPublicationsForMe,
    ],
  );

  return (
    <PublicationsForMeContext value={contextValue}>
      {children}
    </PublicationsForMeContext>
  );
};
